/*  ========================================================================
    # Components - Production Card
    ========================================================================  */

import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import useBreakpoints from '../hooks/useBreakpoints';
import useTranslation from '../hooks/useTranslation';
import LazyImage from './LazyImage';

const ProductionCard = ({
  hideSegment,
  image,
  title,
  segment,
  description,
  url,
  nextDate,
  subtitle,
  video
}) => {
  const __ = useTranslation();
  const { ref: cardRef, inView } = useInView({
    rootMargin: '300px',
    threshold: 0,
    triggerOnce: true
  });
  const descriptionRef = useRef(null);
  const videoRef = useRef(null);
  const [descriptionHeight, setDescriptionHeight] = useState(0);
  const [cardCanAnimate, setCardCanAnimate] = useState(false);
  const [cardHover, setCardHover] = useState(false);
  const { xxxlUp, xxlUp } = useBreakpoints();
  const locale =
    navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;
  const date = new Date(nextDate?.year, nextDate?.month - 1, nextDate?.day);
  const currentDate = new Date();
  const updateDescriptionHeight = useCallback(() => {
    setDescriptionHeight(descriptionRef.current?.clientHeight);
  }, [setDescriptionHeight]);

  const getProductionDate = () => {
    if (date.getFullYear() === currentDate.getFullYear()) {
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long'
      });
    } else {
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  };

  // Trigger size calculation on resize
  useEffect(() => {
    updateDescriptionHeight();
    window.addEventListener('resize', updateDescriptionHeight, { passive: true });
    return () => window.removeEventListener('resize', updateDescriptionHeight);
  }, [updateDescriptionHeight, inView]);

  // Enable card hover effect after a short while to avoid sliding glitch
  useEffect(() => {
    if (inView && descriptionHeight > 0) {
      setTimeout(() => setCardCanAnimate(true), 20);
    }
  }, [inView, descriptionHeight]);

  useEffect(() => {
    if (videoRef.current && cardHover) {
      videoRef.current.play();
    } else if (videoRef.current && !cardHover) {
      videoRef.current.pause();
    }
  }, [cardHover, videoRef]);

  return (
    <a
      ref={cardRef}
      href={url}
      aria-label={title}
      className={['c-production-card', hideSegment && !subtitle && 'c-production-card--no-segment']
        .filter(Boolean)
        .join(' ')}
      style={{ '--description-height': descriptionHeight + 'px' }}
      onMouseEnter={() => {
        setCardHover(true);
      }}
      onMouseLeave={() => {
        setCardHover(false);
      }}
    >
      <div className='c-production-card__date'>
        {nextDate
          ? __('production_card_next_date', { date: getProductionDate() })
          : __('production_card_no_next_date')}
      </div>
      {image && (
        <LazyImage
          defaultUrl={image.sizes.medium_large}
          placeholderUrl={image.sizes.placeholder}
          figureClass='c-production-card__image'
          alt={title}
        ></LazyImage>
      )}
      {video && (
        <div
          className={`c-production-card__video ${cardHover ? 'c-production-card__video--show' : ''
            }`}
        >
          <picture>
            <video src={video.url} title={video.title} loop muted ref={videoRef}></video>
          </picture>
        </div>
      )}
      <div className='c-production-card__content' data-can-translate={cardCanAnimate}>
        <h2
          className='h3 c-production-card__title'
          dangerouslySetInnerHTML={{ __html: title }}
        ></h2>
        {subtitle && <p>{subtitle}</p>}
        {!hideSegment && (
          <span
            className='c-label c-label--badge'
            style={{
              '--label-badge-color': segment.color,
              '--label-badge-background': '#ffffff'
            }}
          >
            {segment.title}
          </span>
        )}
        <div className='c-production-card__description' ref={descriptionRef}>
          {xxlUp && description && <p className='c-production-card__excerpt'>{description}</p>}
          {xxxlUp && (
            <button
              className={`
                c-button c-button--white-outline
                ${cardHover ? 'c-button--hover c-button--white-outline--hover' : ''}
              `}
              aria-label={__('production_card_button_text')}
              tabIndex={-1}
              aria-hidden={true}
            >
              {__('production_card_button_text')}
            </button>
          )}
        </div>
      </div>
    </a>
  );
};

ProductionCard.propTypes = {
  hideSegment: PropTypes.bool,
  title: PropTypes.string,
  image: PropTypes.object,
  segment: PropTypes.object,
  url: PropTypes.string,
  description: PropTypes.string,
  nextDate: PropTypes.object,
  subtitle: PropTypes.string,
  video: PropTypes.object
};

export default ProductionCard;
